import { useEffect } from "react";

/**
 * RELOAD WHEN A NEW BUILD IS SERVING, WITHOUT BEING ASKED.
 *
 * A deploy replaces the files on the server and changes nothing in a tab that
 * is already open. The reader keeps using the old bundle indefinitely — every
 * fix shipped today was invisible to anyone who had not thought to
 * hard-refresh, which is not a thing a person should have to think about.
 *
 * HOW IT KNOWS. Vite fingerprints the entry bundle, so `index.html` names a
 * different file after every build that changed anything. `index.html` itself
 * is served `no-cache` (see the nginx config in deploy/web.sh), so re-fetching
 * it is a cheap, honest answer to "what would a fresh visitor get". Comparing
 * that filename to the one this page loaded is the whole mechanism — no
 * version endpoint to keep in sync, no build stamp to forget to bump.
 *
 * WHY IT WAITS FOR AN IDLE MOMENT. Reloading mid-interaction is worse than a
 * stale bundle: it discards a drag, a filter, an open dropdown. So a pending
 * reload sits until the tab has been in the background, which is the one moment
 * a reload costs nothing and is also when most deploys land.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It never reloads on a network error or a
 * non-200 — a blip during a deploy would otherwise reload every open tab into
 * whatever half-state the rollout was in. An unknown answer means "do nothing",
 * which is the same rule the rest of this product uses for a number it cannot
 * verify.
 */

/** How often to ask. A deploy is rare; this is a background courtesy. */
const CHECK_MS = 60_000;

/**
 * The entry bundle this page is running, read from the DOM rather than
 * injected at build time — the tag is already there and cannot disagree with
 * what actually loaded.
 */
function currentBundle(): string | null {
  const scripts = [...document.querySelectorAll("script[src]")];
  for (const script of scripts) {
    const src = script.getAttribute("src") ?? "";
    if (/\/assets\/index-[A-Za-z0-9_-]+\.js$/.test(src)) return src;
  }
  return null;
}

/** The entry bundle a fresh visitor would get right now. */
async function servedBundle(signal: AbortSignal): Promise<string | null> {
  const response = await fetch("/", { cache: "no-store", signal });
  if (!response.ok) return null;
  const html = await response.text();
  return /\/assets\/index-[A-Za-z0-9_-]+\.js/.exec(html)?.[0] ?? null;
}

export function useLatestBuild(): void {
  useEffect(() => {
    const mine = currentBundle();
    // No fingerprint means a dev server or an unexpected shape. Do nothing
    // rather than guess: a reload loop is far worse than a stale tab.
    if (mine === null) return;

    const controller = new AbortController();
    let stale = false;

    const reloadWhenIdle = (): void => {
      if (document.visibilityState === "hidden") {
        window.location.reload();
      } else {
        stale = true;
      }
    };

    const check = async (): Promise<void> => {
      try {
        const serving = await servedBundle(controller.signal);
        if (serving !== null && serving !== mine) reloadWhenIdle();
      } catch {
        // Offline, aborted, or mid-rollout. Ask again next tick.
      }
    };

    const onHidden = (): void => {
      if (stale && document.visibilityState === "hidden") {
        window.location.reload();
      }
    };

    const timer = window.setInterval(() => void check(), CHECK_MS);
    document.addEventListener("visibilitychange", onHidden);
    void check();

    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, []);
}
